<script setup lang="ts">
/**
 * Onglet « Factures » de la page compteur — réutilise l'espace invoice-only
 * filtré sur le compteur courant (`?pdl=`). Même contrat, même composant
 * (`FacturesList`) que l'espace company-wide ; AUCUN verdict/anomalie (R1).
 *
 * Branché via une route enfant de la fiche source (cf. `source.routes.ts`),
 * gatée sur le module `analyse-facture`. La visibilité par compteur reste
 * décidée côté api-metier (un compteur en `views` seulement → factures
 * masquées, même ici).
 */
import { computed, onMounted, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { storeToRefs } from "pinia"
import { useSourceStore } from "@core/stores/source.store"
import FacturesList from "@modules/courtage/components/factures/FacturesList.vue"
import ModalAjouterCompte from "@modules/courtage/components/factures/ModalAjouterCompte.vue"
import { useEspaceFacturesStore } from "@modules/courtage/stores/espace-factures.store"
import type { FactureItem } from "@modules/courtage/types/facture-espace"

defineOptions({ name: "PageSourceFactures" })

const aAjouter = ref<FactureItem | null>(null)
const confirmation = ref<string | null>(null)

const route = useRoute()
const router = useRouter()
const sourceStore = useSourceStore()
const { getSourceById } = storeToRefs(sourceStore)
const espaceStore = useEspaceFacturesStore()
const { factures, loading, error } = storeToRefs(espaceStore)

const sourceId = computed<string | undefined>(
    () => route.params._id_source as string | undefined
)

/** PDL/PCE du compteur courant = filtre de la liste. */
const pdl = computed<string | undefined>(() =>
    sourceId.value ? getSourceById.value(sourceId.value)?.idsource : undefined
)

async function load(): Promise<void> {
    if (!pdl.value) return
    await espaceStore.fetchList({ pdl: pdl.value })
}

onMounted(async () => {
    if (sourceId.value && !getSourceById.value(sourceId.value)) {
        await sourceStore.fetchSourceById({ _id_source: sourceId.value })
    }
    await load()
})

watch(pdl, () => {
    void load()
})

function onVoir(f: FactureItem): void {
    // Ouvre le scan original (URL présignée via api-metier → courtage).
    void espaceStore.openPdf(f)
}

function onCta(f: FactureItem): void {
    confirmation.value = null
    aAjouter.value = f
}

function onConfirmAjout(f: FactureItem): void {
    aAjouter.value = null
    confirmation.value = `Demande d'ajout notée pour le compteur ${f.pdl}. Nous revenons vers vous pour la signature du consentement.`
}

function voirToutes(): void {
    void router.push("/courtage/mes-factures")
}
</script>

<template>
    <div class="page p-6">
        <div class="rounded-lg border border-neutral-200 bg-white p-5">
            <header class="mb-4 flex items-center justify-between gap-3">
                <h2 class="m-0 text-lg font-medium text-primary-900">
                    <i class="pi pi-file" aria-hidden="true" />
                    Factures du compteur
                </h2>
                <button
                    type="button"
                    class="text-xs font-medium text-cyan-500 hover:text-cyan-600"
                    @click="voirToutes"
                >
                    Voir toutes les factures
                </button>
            </header>

            <div
                v-if="confirmation"
                class="mb-3 flex items-center gap-2 rounded-md border-l-4 border-green-500 bg-green-50 px-4 py-2.5 text-xs text-green-800"
            >
                <i class="pi pi-check-circle" aria-hidden="true" />
                <span>{{ confirmation }}</span>
            </div>

            <FacturesList
                :factures="factures"
                :loading="loading"
                :show-compteur="false"
                @voir="onVoir"
                @cta="onCta"
            />

            <p v-if="error" class="mt-3 text-xs text-red-600">
                {{ error }}
            </p>
        </div>

        <ModalAjouterCompte
            :model-value="aAjouter !== null"
            :facture="aAjouter"
            @update:model-value="
                (v) => {
                    if (!v) aAjouter = null
                }
            "
            @confirm="onConfirmAjout"
        />
    </div>
</template>
